<template>
  <div class="home-view">
    <v-sheet
      class="hero text-center"
      elevation="0"
      :style="heroBackgroundStyle"
    >
      <div class="hero-overlay">
        <v-container class="hero-shell">
          <h1 class="hero-title">Premium Landscaping and Outdoor Services</h1>
          <p class="hero-copy mx-auto">
            Stone Age Landscapes designs, builds, and maintains distinctive
            outdoor spaces year-round, with dependable service for both
            landscape projects and winter snow management.
          </p>
          <v-row class="justify-center hero-cta-row" dense>
            <v-col cols="12" sm="auto">
              <v-btn
                class="hero-btn hero-btn-call"
                :href="phoneHref"
                size="large"
              >
                Speak with our team
              </v-btn>
            </v-col>
            <v-col v-if="isEstimateFormEnabled" cols="12" sm="auto">
              <v-btn
                class="hero-btn hero-btn-estimate"
                @click="scrollToEstimate"
                size="large"
              >
                Request estimate
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-sheet>

    <v-container class="trust-band" fluid>
      <v-row justify="center" align="center" class="text-center">
        <v-col cols="12" md="4" class="trust-item">Established 2011</v-col>
        <v-col cols="12" md="4" class="trust-item"
          >Serving Minneapolis + Surrounding Suburbs</v-col
        >
        <v-col cols="12" md="4" class="trust-item">Licensed and Insured</v-col>
      </v-row>
    </v-container>

    <section class="services-section">
      <div class="services-body">
        <div class="services-left-panel">
          <div class="services-header">
            <p class="services-kicker">Tailored Outdoor Environments</p>
            <h2 class="services-title">Our Services</h2>
            <p class="services-subtitle">
              Purpose-built landscaping, excavation, and seasonal care designed
              to elevate your property and hold up beautifully year after year.
            </p>
          </div>

          <div class="services-grid">
            <!-- Featured: Landscaping spans the full left column height -->
            <div class="service-card-featured">
              <v-img
                :src="orderedServices[0].image"
                :alt="orderedServices[0].alt"
                class="featured-card-image"
                cover
              ></v-img>
              <div class="featured-card-body">
                <div class="service-header-row">
                  <v-icon class="service-card-icon">{{
                    orderedServices[0].icon
                  }}</v-icon>
                  <h3 class="service-card-title">
                    {{ orderedServices[0].title }}
                  </h3>
                </div>
                <p class="service-card-desc">
                  {{ orderedServices[0].description }}
                </p>
              </div>
            </div>

            <!-- Remaining 2 services stack in the right column -->
            <div
              v-for="service in orderedServices.slice(1)"
              :key="service.title"
              class="service-card-small"
            >
              <v-img
                :src="service.image"
                :alt="service.alt"
                class="service-card-image"
                height="138"
                cover
              ></v-img>
              <div class="service-card-body">
                <div class="service-header-row">
                  <v-icon class="service-card-icon">{{ service.icon }}</v-icon>
                  <h3 class="service-card-title">{{ service.title }}</h3>
                </div>
                <p class="service-card-desc">{{ service.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="services-right-sidebar">
          <div class="sidebar-content">
            <div class="sidebar-logo">
              <v-icon class="leaf-icon">mdi-leaf</v-icon>
            </div>

            <div class="sidebar-trust">
              <h4 class="trust-label">
                Proven Craftsmanship. Reliable Follow-Through
              </h4>
              <ul class="trust-list">
                <li class="sidebar-trust-item">Design + build expertise</li>
                <li class="sidebar-trust-item">Reliable seasonal service</li>
                <li class="sidebar-trust-item">Clean, professional crews</li>
              </ul>
            </div>

            <div class="gallery-showcase">
              <h4 class="gallery-showcase-label">Featured Work</h4>
              <div class="gallery-grid">
                <router-link
                  v-for="image in featuredGalleryImages"
                  :key="image.src"
                  to="/gallery"
                  class="gallery-tile-link"
                >
                  <v-img
                    :src="image.src"
                    :alt="image.alt"
                    aspect-ratio="1"
                    cover
                    class="gallery-tile"
                  ></v-img>
                </router-link>
              </div>
            </div>

            <!-- Gallery CTA -->
            <div class="sidebar-cta">
              <v-btn class="sidebar-gallery-btn" to="/gallery">
                Browse Gallery
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="estimate-form" ref="estimateSection" class="estimate-section">
      <div
        :class="[
          'estimate-shell',
          { 'estimate-shell--contact-only': !isEstimateFormEnabled },
        ]"
      >
        <div class="estimate-copy">
          <div class="estimate-intro">
            <p class="estimate-kicker">
              {{
                isEstimateFormEnabled ? "Request an Estimate" : "Get In Touch!"
              }}
            </p>
            <h2 class="estimate-title">
              {{
                isEstimateFormEnabled
                  ? "Tell us about your project"
                  : "Tell us about your project"
              }}
            </h2>
            <p class="estimate-description">
              {{
                isEstimateFormEnabled
                  ? "Share a few details about your landscaping, excavation, or snow management needs and we will follow up directly."
                  : "Reach out by phone or email to discuss your landscaping, excavation, or snow management needs."
              }}
            </p>
          </div>

          <div class="estimate-contact-card">
            <div class="estimate-details">
              <p v-if="$config.companyInfo.phone" class="estimate-detail">
                <span class="estimate-detail-label">
                  <v-icon class="estimate-detail-icon" size="16"
                    >mdi-phone-outline</v-icon
                  >
                  Call us
                </span>
                <a
                  :href="phoneHref"
                  class="estimate-link estimate-detail-value"
                >
                  {{ $config.companyInfo.phone }}
                </a>
              </p>
              <p v-if="$config.companyInfo.email" class="estimate-detail">
                <span class="estimate-detail-label">
                  <v-icon class="estimate-detail-icon" size="16"
                    >mdi-email-outline</v-icon
                  >
                  Email
                </span>
                <a
                  :href="emailHref"
                  class="estimate-link estimate-detail-value"
                >
                  {{ $config.companyInfo.email }}
                </a>
              </p>
              <p v-if="$config.companyInfo.address" class="estimate-detail">
                <span class="estimate-detail-label">
                  <v-icon class="estimate-detail-icon" size="16"
                    >mdi-map-marker-outline</v-icon
                  >
                  Address
                </span>
                <a
                  :href="mapsLinkUrl"
                  class="estimate-link estimate-detail-value"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ $config.companyInfo.address.street }},
                  {{ $config.companyInfo.address.city }},
                  {{ $config.companyInfo.address.state }}
                  {{ $config.companyInfo.address.zip }}
                </a>
              </p>
            </div>
          </div>
          <div v-if="$config.companyInfo.address" class="estimate-map">
            <iframe
              :src="mapsEmbedUrl"
              width="100%"
              style="border: 0; display: block"
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              title="Stone Age Landscapes location"
            ></iframe>
          </div>
        </div>

        <v-card
          v-if="isEstimateFormEnabled"
          class="estimate-form-card"
          variant="flat"
        >
          <v-form
            ref="form"
            v-model="valid"
            :class="[
              'estimate-form',
              { 'estimate-form--submitted': submitted },
            ]"
          >
            <div class="estimate-form-grid">
              <v-text-field
                v-model="firstName"
                :rules="fieldRules('First Name')"
                label="First Name"
                variant="outlined"
                color="#2f4d3d"
                maxlength="60"
                :readonly="submitted"
                required
              ></v-text-field>
              <v-text-field
                v-model="lastName"
                :rules="fieldRules('Last Name')"
                label="Last Name"
                variant="outlined"
                color="#2f4d3d"
                maxlength="60"
                :readonly="submitted"
                required
              ></v-text-field>
              <v-text-field
                v-model="email"
                :rules="emailRules"
                label="Email"
                variant="outlined"
                color="#2f4d3d"
                type="email"
                inputmode="email"
                maxlength="254"
                validate-on="blur"
                :readonly="submitted"
                required
              ></v-text-field>
              <v-text-field
                v-model="phone"
                :rules="phoneRules"
                label="Phone Number"
                variant="outlined"
                color="#2f4d3d"
                inputmode="tel"
                maxlength="14"
                :readonly="submitted"
                @input="formatPhoneInput"
              ></v-text-field>
            </div>
            <v-select
              v-model="selectedService"
              :items="serviceOptions"
              :rules="fieldRules('Service')"
              label="Service Interested In"
              variant="outlined"
              color="#2f4d3d"
              :readonly="submitted"
              required
            ></v-select>
            <v-textarea
              v-model="message"
              :rules="messageRules"
              :counter="MESSAGE_MAX_LENGTH"
              label="Message"
              rows="5"
              variant="outlined"
              color="#2f4d3d"
              maxlength="1000"
              :readonly="submitted"
            ></v-textarea>
            <div class="estimate-submit-row">
              <v-btn
                class="estimate-submit-btn"
                :disabled="submitted"
                @click="submitForm"
              >
                <v-icon v-if="submitted" start>mdi-check-circle</v-icon>
                {{ submitted ? "Request Sent" : "Submit Request" }}
              </v-btn>
              <p v-if="submitted" class="estimate-success-msg">
                Thanks! We'll be in touch soon.
              </p>
            </div>
          </v-form>
        </v-card>
      </div>
    </section>
  </div>
</template>

<script>
import { defineComponent } from "vue";

export default defineComponent({
  name: "HomeView",
  computed: {
    isEstimateFormEnabled() {
      return this.$config.moduleActivation?.contactUs !== false;
    },
    phoneHref() {
      const digits = (this.$config.companyInfo.phone || "").replace(/\D/g, "");
      if (!digits) return "tel:";
      return digits.length === 10 ? `tel:+1${digits}` : `tel:${digits}`;
    },
    emailHref() {
      const email = this.$config.companyInfo.email || "";
      return `mailto:${email}`;
    },
    mapsEmbedUrl() {
      const addr = this.$config.companyInfo.address;
      if (!addr) return "";
      const q = encodeURIComponent(
        `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`
      );
      return `https://maps.google.com/maps?q=${q}&output=embed&z=15`;
    },
    mapsLinkUrl() {
      const addr = this.$config.companyInfo.address;
      if (!addr) return "#";
      const q = encodeURIComponent(
        `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`
      );
      return `https://maps.google.com/maps?q=${q}`;
    },
    heroBackgroundStyle() {
      const heroImage = `${process.env.BASE_URL}photos/landscaping1.jpg`;
      return {
        backgroundImage: `linear-gradient(rgba(23, 36, 30, 0.15), rgba(23, 36, 30, 0.26)), url('${heroImage}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    },
    orderedServices() {
      return [...this.services].sort((a, b) => a.order - b.order);
    },
    featuredGalleryImages() {
      // Get all service images to exclude
      const serviceImages = this.services.map((s) => s.image);

      // Pool of gallery images to choose from
      const allGalleryOptions = [
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-02.jpg",
          alt: "Stone retaining wall and landscape finishing",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-03.jpg",
          alt: "Landscape transformation with clean edging",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-04.jpg",
          alt: "Outdoor grading and site preparation work",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-05.jpg",
          alt: "Installed pavers and garden bed shaping",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-06.jpg",
          alt: "Mulch installation and planting details",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-07.jpg",
          alt: "Hardscape detail with natural stone accents",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-08.jpg",
          alt: "Excavation and grading project progress",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-10.jpg",
          alt: "Finished landscape build with clean lines",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-11.jpg",
          alt: "Backyard landscaping project complete",
        },
        {
          src: process.env.BASE_URL + "photos/gallery/gallery-12.jpg",
          alt: "Stone patio and landscape construction",
        },
      ];

      // Filter out any images used in services
      const filtered = allGalleryOptions.filter(
        (img) => !serviceImages.includes(img.src)
      );

      // Return first 4 non-overlapping images
      return filtered.slice(0, 4);
    },
    emailRules() {
      return [
        (value) => !!value || "E-mail is required",
        (value) => this.isValidEmail(value) || "E-mail must be valid",
      ];
    },
    phoneRules() {
      return [
        (value) =>
          !value ||
          /^\(\d{3}\) \d{3}-\d{4}$/.test(value) ||
          "Enter a valid 10-digit phone number",
      ];
    },
    messageRules() {
      return [
        (value) =>
          !value ||
          value.length <= this.MESSAGE_MAX_LENGTH ||
          `Message must be ${this.MESSAGE_MAX_LENGTH} characters or fewer`,
        (value) =>
          !value ||
          !/<[^>]*>/g.test(value) ||
          "Message must not contain HTML or script content",
      ];
    },
  },
  data() {
    return {
      MESSAGE_MAX_LENGTH: 1000,
      EMAIL_MAX_LENGTH: 254,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      selectedService: "",
      message: "",
      valid: false,
      submitted: false,
      serviceOptions: ["Landscaping", "Snow Management", "Excavation", "Other"],
      services: [
        {
          title: "Landscaping",
          icon: "mdi-sprout",
          image: process.env.BASE_URL + "photos/gallery/gallery-01.jpg",
          alt: "Landscaping installation with curated plantings",
          description:
            "Planting, grading, and outdoor layout work designed for clean, durable results.",
          order: 1,
        },
        {
          title: "Snow Management",
          icon: "mdi-snowflake",
          image: process.env.BASE_URL + "photos/plowing1.jpg",
          alt: "Snow plowing service clearing a residential driveway",
          description:
            "Dependable winter plowing and snow response for residential and commercial lots.",
          order: 2,
        },
        {
          title: "Excavation",
          icon: "mdi-excavator",
          image: process.env.BASE_URL + "photos/gallery/gallery-09.jpg",
          alt: "Excavation crew preparing the ground for landscape work",
          description:
            "Precision grading and excavation that sets a stable base for outdoor builds.",
          order: 3,
        },
      ],
    };
  },
  methods: {
    fieldRules(fieldLabel) {
      return [(value) => !!value || `${fieldLabel} is required`];
    },
    formatPhoneInput() {
      const digits = this.phone.replace(/\D/g, "").slice(0, 10);
      if (digits.length <= 3) {
        this.phone = digits.length ? `(${digits}` : "";
      } else if (digits.length <= 6) {
        this.phone = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        this.phone = `(${digits.slice(0, 3)}) ${digits.slice(
          3,
          6
        )}-${digits.slice(6)}`;
      }
    },
    isValidEmail(email) {
      if (typeof email !== "string") return false;

      const trimmed = email.trim();
      if (!trimmed || trimmed.length > this.EMAIL_MAX_LENGTH) return false;
      if (trimmed.includes(" ")) return false;

      const atIndex = trimmed.indexOf("@");
      const hasSingleAt = atIndex > 0 && atIndex === trimmed.lastIndexOf("@");
      if (!hasSingleAt) return false;

      const local = trimmed.slice(0, atIndex);
      const domain = trimmed.slice(atIndex + 1);
      if (!local || !domain || local.length > 64) return false;
      if (
        local.startsWith(".") ||
        local.endsWith(".") ||
        local.includes("..")
      ) {
        return false;
      }

      const domainParts = domain.split(".");
      if (domainParts.length < 2) return false;
      if (domainParts.some((part) => !part)) return false;

      const domainPartPattern = /^[A-Za-z0-9-]+$/;
      const hasValidDomain = domainParts.every(
        (part) =>
          domainPartPattern.test(part) &&
          !part.startsWith("-") &&
          !part.endsWith("-")
      );

      return hasValidDomain;
    },
    scrollToEstimate() {
      const section = this.$refs.estimateSection;
      if (!section) return;

      section.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async submitForm() {
      const form = this.$refs.form;
      if (!form) return;

      const result = await form.validate();
      if (result.valid && this.isValidEmail(this.email)) {
        this.submitted = true;
      }
    },
  },
});
</script>

<style scoped>
.home-view {
  padding-bottom: 0;
}

.hero {
  background-color: #2f4d3d;
}

.hero-shell {
  padding: 134px 24px 56px;
  max-width: 1420px;
  margin: 0 auto;
}

.hero-overlay {
  background: linear-gradient(
    180deg,
    rgba(23, 36, 30, 0.08),
    rgba(23, 36, 30, 0.22)
  );
}

.hero-title {
  max-width: 900px;
  margin: 0 auto 16px;
  color: #fff;
  text-shadow: 0 2px 10px rgba(13, 20, 16, 0.48);
  font-size: clamp(2rem, 4vw, 3.6rem);
  line-height: 1.05;
  font-weight: 600;
}

.hero-copy {
  max-width: 760px;
  margin-bottom: 24px;
  color: rgba(255, 255, 255, 0.94);
  text-shadow: 0 1px 8px rgba(12, 19, 15, 0.4);
  font-size: 1.2rem;
  line-height: 1.5;
}

.hero-cta-row {
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
  gap: 10px;
}

.hero-btn {
  min-width: 252px;
  min-height: 58px;
  border-radius: 999px;
  padding: 0 30px;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 1.03rem;
  letter-spacing: 0.045em;
  border: 1.5px solid transparent;
  box-shadow: 0 8px 20px rgba(18, 29, 24, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.hero-btn:hover {
  transform: translateY(-2px);
}

.hero-btn-call {
  background: linear-gradient(180deg, #c8a45f 0%, #b58844 100%) !important;
  color: #1a2b22 !important;
  border-color: rgba(228, 206, 156, 0.95) !important;
  box-shadow: 0 10px 24px rgba(13, 20, 17, 0.32),
    inset 0 1px 0 rgba(255, 244, 212, 0.55);
}

.hero-btn-call:hover {
  background: linear-gradient(180deg, #d3b271 0%, #bc9151 100%) !important;
  border-color: rgba(236, 218, 176, 0.95) !important;
  box-shadow: 0 14px 28px rgba(13, 20, 17, 0.36),
    inset 0 1px 0 rgba(255, 244, 212, 0.62);
}

.hero-btn-estimate {
  background: rgba(21, 36, 29, 0.62) !important;
  color: #ead9b3 !important;
  border-color: rgba(232, 207, 149, 0.74) !important;
  box-shadow: 0 10px 24px rgba(8, 14, 11, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.09);
}

.hero-btn-estimate:hover {
  background: rgba(15, 29, 23, 0.8) !important;
  color: #f4e2bc !important;
  border-color: rgba(240, 220, 179, 0.9) !important;
  box-shadow: 0 14px 28px rgba(8, 14, 11, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.trust-band {
  background: #1e3328;
  color: #f1eadf;
  padding-top: 7px;
  padding-bottom: 7px;
}

.trust-band :deep(.v-container) {
  max-width: 1420px;
  margin: 0 auto;
}

.trust-item {
  font-size: 0.95rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.services-section {
  position: relative;
  padding: 36px var(--sal-section-gutter-desktop) var(--sal-section-gap-desktop);
  background-color: #e4dac8;
  background-image: radial-gradient(
      circle at 18% 24%,
      rgba(255, 255, 255, 0.22) 0,
      rgba(255, 255, 255, 0) 34%
    ),
    radial-gradient(
      circle at 78% 16%,
      rgba(120, 108, 88, 0.13) 0,
      rgba(120, 108, 88, 0) 32%
    ),
    radial-gradient(
      circle at 30% 78%,
      rgba(116, 105, 86, 0.12) 0,
      rgba(116, 105, 86, 0) 36%
    ),
    linear-gradient(
      180deg,
      rgba(245, 239, 226, 0.62),
      rgba(222, 210, 188, 0.84)
    );
  overflow: hidden;
}

.services-section::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.4;
  background-image: repeating-linear-gradient(
      23deg,
      rgba(84, 73, 55, 0.08) 0 2px,
      rgba(84, 73, 55, 0) 2px 24px
    ),
    repeating-linear-gradient(
      -18deg,
      rgba(255, 255, 255, 0.12) 0 1px,
      rgba(255, 255, 255, 0) 1px 22px
    );
}

.services-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  align-items: stretch;
  width: 100%;
  max-width: 1420px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.services-left-panel {
  padding: 0 30px 0 0;
  max-width: none;
  width: 100%;
  justify-self: auto;
}

.services-header {
  text-align: center;
  margin-bottom: 24px;
}

.services-kicker {
  margin: 0 0 7px;
  font-size: 0.76rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  font-weight: 700;
  color: #9c7743;
}

.services-title {
  font-size: 2.15rem;
  font-weight: 600;
  color: #2b2a27;
  margin-bottom: 10px;
  line-height: 1.15;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
}

.services-title::after {
  content: "";
  display: block;
  width: 68px;
  height: 2px;
  margin: 14px auto 0;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(171, 128, 69, 0.12) 0%,
    rgba(171, 128, 69, 0.92) 50%,
    rgba(171, 128, 69, 0.12) 100%
  );
}

.services-subtitle {
  max-width: 760px;
  margin: 0 auto;
  font-size: 1.02rem;
  line-height: 1.62;
  color: #5a5a55;
}

.services-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(2, minmax(250px, 1fr));
  gap: 14px;
  align-items: stretch;
}

.service-card-featured {
  grid-column: 1;
  grid-row: 1 / 3;
  background: linear-gradient(180deg, #fcf8f1 0%, #f2e9db 58%, #ecdfcc 100%);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid rgba(137, 112, 76, 0.24);
  box-shadow: 0 12px 26px rgba(29, 24, 18, 0.12),
    inset 0 1px 0 rgba(255, 252, 246, 0.7);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
  position: relative;
}

.service-card-featured::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.26) 0%,
    rgba(255, 255, 255, 0) 22%
  );
}

.service-card-featured:hover {
  box-shadow: 0 12px 26px rgba(29, 24, 18, 0.12),
    0 0 0 1px rgba(156, 119, 67, 0.38), inset 0 1px 0 rgba(255, 252, 246, 0.82),
    inset 0 0 28px rgba(212, 167, 100, 0.07);
}

.featured-card-image {
  min-height: 280px;
  flex: 0 0 auto;
}

.featured-card-body {
  padding: 18px 20px 20px;
  flex: 1;
  position: relative;
  z-index: 1;
}

.service-card-small {
  grid-column: 2;
  background: linear-gradient(180deg, #fdfaf5 0%, #f2e8db 62%, #ede0ce 100%);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid rgba(137, 112, 76, 0.22);
  box-shadow: 0 11px 22px rgba(29, 24, 18, 0.1),
    inset 0 1px 0 rgba(255, 252, 246, 0.65);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
  position: relative;
}

.service-card-small::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.24) 0%,
    rgba(255, 255, 255, 0) 24%
  );
}

.service-card-small:hover {
  box-shadow: 0 11px 22px rgba(29, 24, 18, 0.1),
    0 0 0 1px rgba(156, 119, 67, 0.36), inset 0 1px 0 rgba(255, 252, 246, 0.78),
    inset 0 0 22px rgba(212, 167, 100, 0.06);
}

.service-card-body {
  padding: 14px 16px 16px;
  position: relative;
  z-index: 1;
}

.service-header-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.service-card-icon {
  color: #83633a;
  font-size: 22px;
  flex-shrink: 0;
}

.service-card-title {
  font-size: 1.08rem;
  font-weight: 600;
  color: #2e2a24;
  margin: 0;
  font-family: "Cormorant Garamond", Georgia, "Times New Roman", serif;
  letter-spacing: 0.01em;
}

.service-card-image {
  border-radius: 0;
  margin: 0;
  overflow: hidden;
}

.service-card-desc {
  font-size: 0.91rem;
  color: #585147;
  line-height: 1.52;
  margin: 3px 0 0;
}

.services-right-sidebar {
  background: linear-gradient(180deg, #2f5442 0%, #274536 100%);
  color: #f5f5f5;
  padding: 30px 22px;
  position: relative;
  z-index: 1;
  border-left: 1px solid rgba(226, 203, 156, 0.3);
  box-shadow: inset 12px 0 16px rgba(12, 21, 16, 0.16),
    inset 0 1px 0 rgba(248, 237, 211, 0.07);
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sidebar-logo {
  text-align: center;
  margin-bottom: 2px;
}

.leaf-icon {
  font-size: 40px;
  color: #b78246;
}

.sidebar-trust {
  padding-top: 0;
}

.trust-label {
  margin: 0 0 10px;
  padding-bottom: 8px;
  font-family: "Cormorant Garamond", Georgia, "Times New Roman", serif;
  font-size: 1.42rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #edd4a8;
  line-height: 1.18;
  text-transform: none;
  position: relative;
}

.trust-label::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 58px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, #d1a567 0%, rgba(209, 165, 103, 0.3) 100%);
}

.trust-list {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
}

.sidebar-trust-item {
  font-size: 0.8rem;
  color: #e8dcc8;
  line-height: 1.4;
}

.sidebar-cta {
  margin-top: 0;
  padding-top: 10px;
  border-top: 1px solid rgba(232, 220, 200, 0.2);
  text-align: center;
}

.gallery-showcase {
  margin-top: 2px;
  padding-top: 10px;
  border-top: 1px solid rgba(232, 220, 200, 0.2);
}

.gallery-showcase-label {
  margin: 0 0 10px;
  padding-bottom: 8px;
  font-family: "Cormorant Garamond", Georgia, "Times New Roman", serif;
  font-size: 1.26rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #edd4a8;
  line-height: 1.18;
  text-transform: none;
  position: relative;
}

.gallery-showcase-label::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, #d1a567 0%, rgba(209, 165, 103, 0.3) 100%);
}

.gallery-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
}

.gallery-tile-link {
  text-decoration: none;
  display: block;
  overflow: hidden;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s ease, filter 0.2s ease;
}

.gallery-tile-link:hover {
  transform: scale(1.05);
  filter: brightness(1.1);
}

.gallery-tile {
  transition: transform 0.2s ease;
}

.sidebar-gallery-btn {
  min-height: 48px;
  width: 100%;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  background: linear-gradient(180deg, #d3b271 0%, #bc9151 100%) !important;
  color: #1a2b22 !important;
  border: 1.5px solid rgba(236, 218, 176, 0.92);
  box-shadow: 0 10px 24px rgba(13, 20, 17, 0.32),
    inset 0 1px 0 rgba(255, 244, 212, 0.55);
}

.sidebar-gallery-btn:hover {
  background: linear-gradient(180deg, #dec086 0%, #c39a5b 100%) !important;
}

.estimate-section {
  background: linear-gradient(180deg, #ece3d1 0%, #e2d5bc 100%);
  padding: var(--sal-section-gap-desktop) var(--sal-section-gutter-desktop) 34px;
}

.estimate-shell {
  max-width: 1420px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 20px;
  align-items: stretch;
}

.estimate-shell--contact-only {
  grid-template-columns: minmax(0, 1fr);
}

.estimate-copy {
  padding: 4px;
  display: flex;
  flex-direction: column;
}

.estimate-intro {
  display: flex;
  flex-direction: column;
}

.estimate-contact-card {
  background: rgba(250, 246, 237, 0.72);
  border: 1px solid rgba(151, 121, 79, 0.18);
  border-radius: 18px;
  padding: 22px 22px 20px;
  box-shadow: 0 18px 34px rgba(38, 30, 20, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.52);
}

.estimate-shell--contact-only .estimate-copy {
  display: grid;
  grid-template-columns: minmax(320px, 0.88fr) minmax(420px, 1.12fr);
  gap: 24px 34px;
  align-items: start;
}

.estimate-shell--contact-only .estimate-intro {
  grid-column: 1;
  grid-row: 1;
  padding-top: 8px;
}

.estimate-shell--contact-only .estimate-contact-card {
  grid-column: 1;
  grid-row: 2;
  max-width: 520px;
}

.estimate-shell--contact-only .estimate-map {
  grid-column: 2;
  grid-row: 1 / span 2;
  margin-top: 0;
  min-height: 360px;
  height: 100%;
  align-self: stretch;
  background: rgba(250, 246, 237, 0.32);
}

.estimate-kicker {
  margin: 0 0 8px;
  color: #b78246;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.estimate-title {
  margin: 0 0 12px;
  color: #20362b;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.05;
}

.estimate-description {
  max-width: 40rem;
  margin: 0 0 16px;
  color: #4b564f;
  font-size: 1rem;
  line-height: 1.6;
}

.estimate-details {
  display: grid;
  gap: 14px;
  max-width: 42rem;
}

.estimate-contact-card .estimate-details {
  max-width: none;
}

.estimate-detail {
  margin: 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: 8px;
  row-gap: 6px;
}

.estimate-detail-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    rgba(183, 130, 70, 0.2) 0%,
    rgba(183, 130, 70, 0.08) 100%
  );
  border: 1px solid rgba(183, 130, 70, 0.28);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.42);
  color: #294235;
  font-size: 0;
  line-height: 1;
  width: fit-content;
  min-width: 0;
}

.estimate-detail-icon {
  color: #9e6f3e;
  opacity: 0.95;
  font-size: 1.2rem !important;
}

.estimate-link {
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease, text-decoration-color 0.2s ease;
}

.estimate-detail-value {
  color: #2f4d3d;
  font-size: 1.06rem;
  font-weight: 600;
  line-height: 1.45;
  display: block;
  min-width: 0;
}

.estimate-link:hover {
  color: #20362b;
  text-decoration: underline;
  text-decoration-color: rgba(183, 130, 70, 0.82);
  text-underline-offset: 0.14em;
}

.estimate-map {
  margin-top: 20px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(47, 77, 61, 0.22);
  box-shadow: 0 8px 28px rgba(20, 30, 25, 0.14),
    inset 0 0 0 3px rgba(195, 154, 91, 0.13);
  flex: 1;
  min-height: 120px;
  position: relative;
}

.estimate-map::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 10px;
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    transparent 55%,
    rgba(23, 36, 30, 0.18) 100%
  );
}

.estimate-map iframe {
  height: 100%;
  min-height: 120px;
  display: block;
  filter: saturate(0.82) sepia(0.12) brightness(0.97);
}

.estimate-form-card {
  border-radius: 14px;
  padding: 18px;
  background: rgba(250, 246, 237, 0.96);
  border: 1px solid rgba(47, 77, 61, 0.18);
  box-shadow: 0 14px 36px rgba(20, 30, 25, 0.12);
}

.estimate-form {
  display: grid;
  gap: 10px;
}

.estimate-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.estimate-form--submitted .estimate-form-grid,
.estimate-form--submitted .v-select,
.estimate-form--submitted .v-textarea {
  filter: grayscale(0.6);
  opacity: 0.62;
  pointer-events: none;
}

.estimate-submit-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.estimate-success-msg {
  margin: 0;
  font-size: 0.95rem;
  color: #2f4d3d;
  font-weight: 500;
  animation: fadeInUp 0.3s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.estimate-submit-btn {
  justify-self: start;
  min-height: 52px;
  border-radius: 999px;
  padding: 0 24px;
  background: linear-gradient(180deg, #1f3429 0%, #16271f 100%) !important;
  color: #ead9b3 !important;
  border: 1.5px solid rgba(232, 207, 149, 0.82);
  box-shadow: 0 10px 24px rgba(8, 14, 11, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.09);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.estimate-submit-btn:hover {
  background: linear-gradient(180deg, #1a2e24 0%, #102019 100%) !important;
  color: #f4e2bc !important;
  border-color: rgba(240, 220, 179, 0.94) !important;
  box-shadow: 0 14px 28px rgba(8, 14, 11, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transform: translateY(-2px);
}

@media (max-width: 1024px) {
  .services-body {
    grid-template-columns: 1fr;
  }

  .services-left-panel {
    padding: 0;
    width: auto;
  }

  .services-right-sidebar {
    padding: 24px;
    border-top: 1px solid rgba(226, 203, 156, 0.22);
    border-left: none;
    box-shadow: inset 0 10px 14px rgba(12, 21, 16, 0.12),
      inset 0 1px 0 rgba(248, 237, 211, 0.07);
  }

  .sidebar-content {
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 16px;
    align-items: center;
    text-align: center;
  }

  .trust-label,
  .gallery-showcase-label {
    text-align: center;
  }

  .trust-label::after,
  .gallery-showcase-label::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .sidebar-description,
  .sidebar-trust,
  .sidebar-cta {
    width: 100%;
    max-width: 620px;
  }

  .trust-list {
    list-style: none;
    padding-left: 0;
    justify-items: center;
  }

  .sidebar-trust-item {
    text-align: center;
  }

  .estimate-shell {
    grid-template-columns: 1fr;
  }

  .estimate-shell--contact-only .estimate-copy {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .estimate-shell--contact-only .estimate-intro,
  .estimate-shell--contact-only .estimate-contact-card,
  .estimate-shell--contact-only .estimate-map {
    grid-column: auto;
    grid-row: auto;
  }

  .estimate-shell--contact-only .estimate-contact-card {
    max-width: none;
  }

  .estimate-shell--contact-only .estimate-map {
    margin-top: 20px;
    min-height: 120px;
    height: auto;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .services-section {
    padding-bottom: var(--sal-section-gap-tablet);
  }

  .services-body {
    row-gap: var(--sal-section-gap-tablet);
  }

  .services-right-sidebar {
    margin-top: 0;
  }

  .estimate-section {
    padding-top: var(--sal-section-gap-tablet);
  }
}

@media (max-width: 768px) {
  .hero-shell {
    padding: 118px 16px 36px;
  }

  .services-section {
    padding: 24px var(--sal-section-gutter-mobile) var(--sal-section-gap-mobile);
  }

  .services-body {
    row-gap: var(--sal-section-gap-mobile);
  }

  .services-left-panel {
    padding: 0;
  }

  .services-right-sidebar {
    padding: 20px 16px;
  }

  .sidebar-content {
    gap: 12px;
    max-width: 460px;
    margin: 0 auto;
  }

  .sidebar-logo {
    margin-bottom: 0;
  }

  .leaf-icon {
    font-size: 34px;
  }

  .sidebar-trust,
  .gallery-showcase,
  .sidebar-cta {
    width: 100%;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
  }

  .gallery-showcase,
  .sidebar-cta {
    margin-top: 0;
    padding-top: 10px;
  }

  .trust-label,
  .gallery-showcase-label {
    text-align: center;
    padding-bottom: 8px;
    margin-bottom: 10px;
  }

  .trust-label::after,
  .gallery-showcase-label::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .services-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  .service-card-featured {
    grid-row: auto;
  }

  .service-card-small {
    grid-column: 1;
  }

  .estimate-section {
    padding: var(--sal-section-gap-mobile) var(--sal-section-gutter-mobile) 24px;
  }

  .estimate-map {
    display: none;
  }

  .estimate-form-card {
    padding: 14px;
  }

  .estimate-submit-btn {
    justify-self: stretch;
    width: 100%;
  }

  .estimate-form-grid {
    grid-template-columns: 1fr;
  }

  .estimate-detail {
    grid-template-columns: 1fr;
    align-items: flex-start;
    row-gap: 8px;
  }

  .estimate-detail-value {
    font-size: 1rem;
  }

  .trust-list {
    list-style: none;
    padding-left: 0;
    gap: 8px;
    justify-items: center;
  }

  .sidebar-trust-item {
    max-width: 32ch;
    font-size: 0.9rem;
    line-height: 1.38;
    text-align: center;
  }

  .gallery-grid {
    gap: 7px;
    margin-bottom: 10px;
  }

  .sidebar-gallery-btn {
    min-height: 46px;
  }
}
</style>
